import React, { ReactNode, useEffect } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { useSupplierBuyerNoticeSummary } from "lib/hooks/api/opportunities/useSupplierBuyerNoticeSummary";
import { useSignalSettingsGQL } from "lib/hooks/api/teams/useSignalSettingsGQL";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { routes } from "lib/routes";
import { irregularPlural, isDefined, pluralise } from "lib/utils";
import { OpportunityDetails } from "../../../lib/hooks/api/opportunities/useOpportunity";
import SupplierSelectPopover from "../../form_components/SupplierSelectPopover";
import Chart, { ChartData } from "./Chart";
import {
  CARD_ICON_COLORS,
  ChartConfigType,
  ChartType,
  getChartConfig,
  getChartData,
} from "./utils";

const ALL_PARTNERS_TOKEN = "SIGNALS:ALL_PARTNERS";

export type OrgDetails = { guid: string; name: string; logoUrl?: string | null };

type EmptyStateConfig = {
  heading: string;
  description: string;
  action?: React.ReactNode;
};

const EMPTY_STATES: Record<string, EmptyStateConfig> = {
  allSuppliers: {
    heading: "We couldn't find any suppliers who have worked with this buyer in this area",
    description: "This could indicate that this buyer hasn't procured a service like this before",
  },
  noPartnerSignals: {
    heading: "You haven't set up your partners",
    description:
      "Update the partners in your signal settings to view which of your partners have worked with this buyer",
    action: (
      <Button buttonType="secondary" as="a" href={routes.settings}>
        Go to signal settings
      </Button>
    ),
  },
  noPartnerRelationships: {
    heading: "You don't have any partners who have worked with this buyer",
    description:
      "This may be because you haven't told us about all your partners. Update your signal settings for a more accurate view",
    action: (
      <Button buttonType="secondary" as="a" href={routes.settings}>
        Go to signal settings
      </Button>
    ),
  },
};

function SupplierRelationshipCharts({
  opportunity,
  isLoading,
}: {
  opportunity: OpportunityDetails;
  isLoading: boolean;
}) {
  const { data: signals } = useSignalSettingsGQL();

  if (opportunity.notices.length === 0) {
    return null;
  }

  // Get the Partners count from prefetched opportunity data
  const partnersWithSummaryData = opportunity?.supplierBuyerNoticeSummary?.filter(
    (summary) => summary.allAwards.totalCount > 0 || summary.similarAwards.totalCount > 0,
  );

  const partnersIdentifiedCount = partnersWithSummaryData?.length ?? 0;

  // Default to the first buyer from the first notice, consistent with buyer activity logic
  const firstNoticeBuyer = opportunity.notices[0].buyers[0];

  const similarNotices = opportunity.buyerActivity?.relatedNotices ?? [];
  const savedNotices = opportunity.notices;
  const allSimilarNotices = [...similarNotices, ...savedNotices];

  const similarNoticeIds = allSimilarNotices.map((notice) => notice.procurementStage.id) ?? [];

  const allSupplierIds =
    allSimilarNotices
      .flatMap((notice) => notice.lots)
      .flatMap((lot) => lot.suppliers)
      .map((supplier) => supplier?.guid)
      .filter(isDefined) ?? [];

  const incumbentSupplierIds = opportunity.suppliers.map((supplier) => supplier.id);

  const hasSupplierAwards = allSupplierIds.length > 0;
  const hasPartnerSignals = (signals?.partners ?? []).length > 0;
  const showPartnersEmptyState = !hasPartnerSignals || partnersIdentifiedCount === 0;

  return (
    <Flex column gap={16}>
      <SupplierChartCard
        title="Contract awards for Suppliers who have worked with this buyer"
        chartType="allSuppliers"
        defaultBuyer={firstNoticeBuyer}
        defaultSupplierIds={allSupplierIds}
        opportunityId={opportunity.id}
        similarNoticeIds={similarNoticeIds}
        incumbentSupplierIds={incumbentSupplierIds}
        emptyState={hasSupplierAwards ? null : <SuppliersEmptyState stateKey="allSuppliers" />}
        isBuyerActivityLoading={isLoading}
      />
      <SupplierChartCard
        title={`${pluralise(partnersIdentifiedCount, "Partner")} who ${irregularPlural(partnersIdentifiedCount, "has", "have")} worked with this buyer`}
        chartType="partners"
        defaultBuyer={firstNoticeBuyer}
        defaultSupplierIds={[ALL_PARTNERS_TOKEN]}
        opportunityId={opportunity.id}
        similarNoticeIds={similarNoticeIds}
        incumbentSupplierIds={incumbentSupplierIds}
        emptyState={
          showPartnersEmptyState ? (
            <PartnerEmptyState
              hasPartnerSignals={hasPartnerSignals}
              partnersIdentifiedCount={partnersIdentifiedCount}
            />
          ) : null
        }
        isBuyerActivityLoading={isLoading}
      />
    </Flex>
  );
}

function SupplierChartCard({
  title,
  chartType,
  opportunityId,
  similarNoticeIds,
  incumbentSupplierIds,
  defaultBuyer,
  defaultSupplierIds,
  emptyState,
  isBuyerActivityLoading,
}: {
  title: ReactNode;
  chartType: ChartType;
  opportunityId: string;
  similarNoticeIds: string[];
  incumbentSupplierIds: string[];
  defaultBuyer: OrgDetails;
  defaultSupplierIds: string[];
  emptyState: JSX.Element | null;
  isBuyerActivityLoading: boolean;
}) {
  const { control, watch, formState, reset } = useForm<{ supplierIds: string[] }>({
    defaultValues: { supplierIds: [] },
  });

  const { supplierIds } = watch();
  const hasEditedSuppliers = formState.isDirty;

  const { data, isLoading } = useSupplierBuyerNoticeSummary(opportunityId, {
    suppliers: hasEditedSuppliers ? supplierIds : defaultSupplierIds,
  });

  useEffect(() => {
    if (supplierIds.length === 0 && !hasEditedSuppliers) {
      const filteredSupplierIds =
        data
          ?.filter(
            (summary) => summary.allAwards.totalCount > 0 || summary.similarAwards.totalCount > 0,
          )
          .map((summary) => summary.supplier.id) ?? [];

      if (!filteredSupplierIds.length) {
        return;
      }

      reset({ supplierIds: filteredSupplierIds });
    }
  }, [reset, data, supplierIds, hasEditedSuppliers]);

  const includeEmptyResults = hasEditedSuppliers ? true : false;
  const chartData = getChartData(data, includeEmptyResults);
  const chartConfig = getChartConfig(chartType);

  return (
    <Card>
      <Header justifyContent="space-between">
        <Flex gap={8} alignItems="center">
          <UIcon icon="presentationChart02" color={CARD_ICON_COLORS[chartType]} size={20} />
          <Text h3>{title}</Text>
        </Flex>
        <SupplierSelectPopover
          title="Add suppliers"
          icon="trophy01"
          label=""
          name="supplierIds"
          control={control}
          categoryOptions={[chartType === "partners" ? "Partner" : "Competitor"]}
          incumbentSuppliers={incumbentSupplierIds}
        />
      </Header>
      <Content>
        {isBuyerActivityLoading ? (
          <SkeletonContainer>
            <SkeletonText width="100%" customSpacing fontSize={135} variant="rectangular" />
          </SkeletonContainer>
        ) : (
          <ChartContainer
            chartType={chartType}
            chartData={chartData}
            selectedBuyer={defaultBuyer}
            similarNoticeIds={similarNoticeIds}
            isLoading={isLoading}
            chartConfig={chartConfig}
            emptyState={!hasEditedSuppliers ? emptyState : null}
          />
        )}
      </Content>
    </Card>
  );
}

function ChartContainer({
  chartType,
  chartData,
  selectedBuyer,
  similarNoticeIds,
  chartConfig,
  isLoading,
  emptyState,
}: {
  chartType: ChartType;
  chartData: ChartData[] | null;
  selectedBuyer: OrgDetails;
  similarNoticeIds: string[];
  isLoading?: boolean;
  chartConfig: ChartConfigType;
  emptyState: JSX.Element | null;
}): JSX.Element | null {
  if (emptyState) {
    return <Flex justifyContent="center">{emptyState}</Flex>;
  }

  if (isLoading) {
    return (
      <SkeletonContainer>
        <SkeletonText width="100%" customSpacing fontSize={60} variant="rectangular" />
        <SkeletonText width="100%" customSpacing fontSize={60} variant="rectangular" />
        <SkeletonText width="100%" customSpacing fontSize={60} variant="rectangular" />
      </SkeletonContainer>
    );
  }

  return (
    <Chart
      chartType={chartType}
      chartData={chartData ?? []}
      chartConfig={chartConfig}
      selectedBuyer={selectedBuyer}
      similarNoticeIds={similarNoticeIds}
    />
  );
}

function SuppliersEmptyState({ stateKey }: { stateKey: string }) {
  const { heading, description, action } = EMPTY_STATES[stateKey];

  return (
    <EmptyStateWrapper>
      <EmptyState
        type="noResults"
        size="large"
        heading={heading}
        description={description}
        action={action}
      />
    </EmptyStateWrapper>
  );
}

function PartnerEmptyState({
  hasPartnerSignals,
  partnersIdentifiedCount,
}: {
  hasPartnerSignals: boolean;
  partnersIdentifiedCount: number;
}) {
  if (!hasPartnerSignals) {
    return <SuppliersEmptyState stateKey="noPartnerSignals" />;
  }
  if (partnersIdentifiedCount === 0) {
    return <SuppliersEmptyState stateKey="noPartnerRelationships" />;
  }
  return null;
}

const Card = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  width: "100%",
  border: `1px solid ${theme.colors.sysBorderPrimary}`,
  borderRadius: "8px",
}));

const Header = styled(Flex)(({ theme }) => ({
  gap: 8,
  alignItems: "center",
  borderBottom: `1px solid ${theme.colors.sysBorderPrimary}`,
  padding: 24,
}));

const Content = styled(Flex)({
  flexDirection: "column",
  padding: "24px 24px 40px 0px",
});

const SkeletonContainer = styled(Flex)({
  flexDirection: "column",
  gap: 8,
  padding: "24px",
});

const EmptyStateWrapper = styled(Flex)({
  textAlign: "center",
});

export default SupplierRelationshipCharts;
